import { supabase } from './supabase'
import type { User } from '../types'

export interface UserDataExport {
  user: {
    id: string
    email: string
    created_at: string
  }
  profile: {
    full_name?: string
    avatar_url?: string
  }
  trips: any[]
  vehicles: any[]
  settings: any
}

export interface CookieConsent {
  necessary: boolean
  functional: boolean
  analytics: boolean
  marketing: boolean
  timestamp: string
}

class PrivacyService {
  private CONSENT_KEY = 'cartrip_cookie_consent'

  // Cookie Consent Management
  getCookieConsent(): CookieConsent | null {
    const stored = localStorage.getItem(this.CONSENT_KEY)
    if (!stored) return null
    
    try {
      return JSON.parse(stored)
    } catch {
      return null
    }
  }

  saveCookieConsent(consent: Omit<CookieConsent, 'timestamp'>) {
    const fullConsent: CookieConsent = {
      ...consent,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem(this.CONSENT_KEY, JSON.stringify(fullConsent))
  }

  hasConsent(): boolean {
    return !!this.getCookieConsent()
  }

  // Data Export (GDPR Article 20 - Right to Data Portability)
  async exportUserData(): Promise<UserDataExport> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No user logged in')

    // Get profile
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, avatar_url')
      .eq('id', user.id)
      .single()

    // Get trips
    const { data: trips } = await supabase
      .from('trips')
      .select('*')
      .eq('user_id', user.id)

    // Get vehicles
    const { data: vehicles } = await supabase
      .from('vehicles')
      .select('*')
      .eq('user_id', user.id)

    // Get settings from localStorage
    const settingsStr = localStorage.getItem('cartrip_settings')
    const settings = settingsStr ? JSON.parse(settingsStr) : {}

    return {
      user: {
        id: user.id,
        email: user.email || '',
        created_at: user.created_at || ''
      },
      profile: profile || {},
      trips: trips || [],
      vehicles: vehicles || [],
      settings
    }
  }

  downloadDataAsJSON(data: UserDataExport, filename = 'my-data.json') {
    const json = JSON.stringify(data, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    
    URL.revokeObjectURL(url)
  }

  // Data Deletion (GDPR Article 17 - Right to Erasure)
  async deleteUserAccount(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No user logged in')

    // Delete trips (cascades to stops via foreign key)
    await supabase
      .from('trips')
      .delete()
      .eq('user_id', user.id)

    // Delete vehicles
    await supabase
      .from('vehicles')
      .delete()
      .eq('user_id', user.id)

    // Delete profile
    await supabase
      .from('profiles')
      .delete()
      .eq('id', user.id)

    // Clear local storage
    localStorage.removeItem('cartrip_settings')
    localStorage.removeItem(this.CONSENT_KEY)

    // Delete auth account (this will sign out the user)
    const { error } = await supabase.auth.admin.deleteUser(user.id)
    if (error) {
      // If admin delete fails (user needs admin privileges), 
      // use the regular delete which requires re-authentication
      throw new Error('Account deletion requires admin privileges. Please contact support.')
    }
  }

  // Email Preferences
  async updateEmailPreferences(preferences: {
    marketing_emails?: boolean
    trip_reminders?: boolean
    weekly_summary?: boolean
  }): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('No user logged in')

    const { error } = await supabase
      .from('profiles')
      .update({
        ...preferences,
        updated_at: new Date().toISOString()
      })
      .eq('id', user.id)

    if (error) throw error
  }

  async getEmailPreferences() {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data } = await supabase
      .from('profiles')
      .select('marketing_emails, trip_reminders, weekly_summary')
      .eq('id', user.id)
      .single()

    return data
  }
}

export const privacyService = new PrivacyService()
